/**
 * 四足插座娃娃：身／頭／尾換來源。
 * 墨線外殼＋較完整剪影，避免只剩膠囊積木。
 */

import { cloneElement, isValidElement, useMemo, useRef, type ReactElement, type ReactNode } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import type { BeastForm, PartRef } from '../content/types'
import { defaultBeastForm, EMBROIDERY, isAnimalSource, SOURCE_BY_ID, type AnimalSourceId } from '../content/partLibrary'
import { HuHead } from '../content/parts/HuHead'

function sourceOf(parts: PartRef[], slot: 'body' | 'head' | 'tail'): AnimalSourceId {
  const p = parts.find(x => x.slot === slot)
  return p && isAnimalSource(p.source) ? p.source : 'yang'
}

function mat(color: string) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.94,
    metalness: 0,
    flatShading: true,
    emissive: new THREE.Color(color),
    emissiveIntensity: 0.08,
  })
}

const outlineMat = new THREE.MeshBasicMaterial({
  color: EMBROIDERY.ink,
  side: THREE.BackSide,
})

function Stroke({ children, ...props }: { children: ReactNode } & Record<string, unknown>) {
  const geo = isValidElement(children) ? children as ReactElement : null
  return (
    <group>
      <mesh {...props}>{geo}</mesh>
      <mesh {...props} material={outlineMat} scale={1.1}>
        {geo ? cloneElement(geo) : null}
      </mesh>
    </group>
  )
}

function Head({ source, clay }: { source: AnimalSourceId; clay: BeastForm }) {
  if (source === 'hu') return <HuHead clay={clay} />
  return <LegacyHead source={source} tint={clay.tint} />
}

function LegacyHead({ source, tint }: { source: AnimalSourceId; tint: string | null }) {
  const def = SOURCE_BY_ID[source]
  const m = useMemo(() => mat(tint ?? def.head), [def.head, tint])
  const ink = useMemo(() => mat(def.accent), [def.accent])
  if (source === 'ren') {
    return (
      <group position={[0.62, 0.38, 0]}>
        <Stroke material={m} position={[0.02, 0.22, 0]}>
          <sphereGeometry args={[0.2, 10, 8]} />
        </Stroke>
        <Stroke material={m} position={[-0.06, 0.02, 0]}>
          <cylinderGeometry args={[0.07, 0.09, 0.18, 8]} />
        </Stroke>
        <mesh material={ink} position={[0.14, 0.26, 0.08]}>
          <sphereGeometry args={[0.028, 6, 4]} />
        </mesh>
        <mesh material={ink} position={[0.14, 0.26, -0.08]}>
          <sphereGeometry args={[0.028, 6, 4]} />
        </mesh>
        <Stroke material={ink} position={[0.16, 0.16, 0]} rotation={[0, 0, Math.PI / 2]}>
          <capsuleGeometry args={[0.018, 0.06, 3, 6]} />
        </Stroke>
      </group>
    )
  }
  if (source === 'yang') {
    return (
      <group position={[0.68, 0.28, 0]}>
        <Stroke material={m} rotation={[0, 0, -0.25]} position={[0.08, 0.02, 0]}>
          <capsuleGeometry args={[0.13, 0.28, 5, 8]} />
        </Stroke>
        <Stroke material={ink} position={[-0.04, 0.26, 0.14]} rotation={[0.55, 0.2, 0.7]}>
          <torusGeometry args={[0.1, 0.028, 6, 10, Math.PI]} />
        </Stroke>
        <Stroke material={ink} position={[-0.04, 0.26, -0.14]} rotation={[-0.55, -0.2, 0.7]}>
          <torusGeometry args={[0.1, 0.028, 6, 10, Math.PI]} />
        </Stroke>
        <mesh material={ink} position={[0.22, 0.08, 0.06]}>
          <sphereGeometry args={[0.02, 5, 4]} />
        </mesh>
        <mesh material={ink} position={[0.22, 0.08, -0.06]}>
          <sphereGeometry args={[0.02, 5, 4]} />
        </mesh>
      </group>
    )
  }
  if (source === 'ma') {
    return (
      <group position={[0.7, 0.32, 0]}>
        <Stroke material={m} position={[-0.08, 0.08, 0]}>
          <cylinderGeometry args={[0.08, 0.1, 0.22, 8]} />
        </Stroke>
        <Stroke material={m} rotation={[0, 0, -0.55]} position={[0.22, -0.02, 0]}>
          <capsuleGeometry args={[0.09, 0.42, 5, 8]} />
        </Stroke>
        <Stroke material={ink} position={[-0.02, 0.28, 0]} rotation={[0, 0, 0.2]}>
          <boxGeometry args={[0.08, 0.16, 0.12]} />
        </Stroke>
        <mesh material={ink} position={[0.38, 0.02, 0.05]}>
          <sphereGeometry args={[0.018, 5, 4]} />
        </mesh>
        <mesh material={ink} position={[0.38, 0.02, -0.05]}>
          <sphereGeometry args={[0.018, 5, 4]} />
        </mesh>
      </group>
    )
  }
  return <HuHead />
}

function Body({ source, tint }: { source: AnimalSourceId; tint: string | null }) {
  const def = SOURCE_BY_ID[source]
  const m = useMemo(() => mat(tint ?? def.body), [def.body, tint])
  const ink = useMemo(() => mat(EMBROIDERY.ink), [])
  if (source === 'ren') {
    return (
      <group>
        <Stroke material={m} position={[0, 0.42, 0]}>
          <capsuleGeometry args={[0.2, 0.38, 5, 10]} />
        </Stroke>
        <Stroke material={m} position={[0, 0.08, 0]}>
          <sphereGeometry args={[0.16, 8, 6]} />
        </Stroke>
      </group>
    )
  }
  if (source === 'ma') {
    return (
      <group position={[0, 0.28, 0]}>
        <Stroke material={m} rotation={[0, 0, Math.PI / 2]}>
          <capsuleGeometry args={[0.2, 0.82, 5, 10]} />
        </Stroke>
        <Stroke material={m} position={[0.28, 0.06, 0]}>
          <sphereGeometry args={[0.18, 8, 6]} />
        </Stroke>
      </group>
    )
  }
  if (source === 'hu') {
    return (
      <group position={[0, 0.26, 0]}>
        <Stroke material={m} rotation={[0, 0, Math.PI / 2]}>
          <capsuleGeometry args={[0.22, 0.62, 5, 10]} />
        </Stroke>
        {[-0.18, 0, 0.18].map((x, i) => (
          <mesh key={i} material={ink} position={[x, 0.02, 0]}>
            <boxGeometry args={[0.05, 0.26, 0.4]} />
          </mesh>
        ))}
      </group>
    )
  }
  return (
    <group position={[0, 0.24, 0]}>
      <Stroke material={m} rotation={[0, 0, Math.PI / 2]}>
        <capsuleGeometry args={[0.24, 0.5, 5, 10]} />
      </Stroke>
      <Stroke material={m} position={[0.22, 0.04, 0]}>
        <sphereGeometry args={[0.16, 8, 6]} />
      </Stroke>
    </group>
  )
}

function Tail({ source, tint }: { source: AnimalSourceId; tint: string | null }) {
  const def = SOURCE_BY_ID[source]
  const m = useMemo(() => mat(tint ?? def.tail), [def.tail, tint])
  if (source === 'ren') return null
  if (source === 'yang') {
    return (
      <Stroke material={m} position={[-0.48, 0.28, 0]}>
        <sphereGeometry args={[0.13, 8, 6]} />
      </Stroke>
    )
  }
  if (source === 'ma') {
    return (
      <group position={[-0.62, 0.12, 0]}>
        <Stroke material={m} rotation={[0, 0, 0.7]}>
          <capsuleGeometry args={[0.035, 0.52, 4, 6]} />
        </Stroke>
        <Stroke material={m} position={[-0.18, -0.16, 0]} rotation={[0, 0, 0.3]}>
          <capsuleGeometry args={[0.05, 0.18, 3, 6]} />
        </Stroke>
      </group>
    )
  }
  return (
    <Stroke material={m} position={[-0.58, 0.22, 0]} rotation={[0, 0, 0.4]}>
      <capsuleGeometry args={[0.055, 0.5, 4, 6]} />
    </Stroke>
  )
}

export function BeastDoll({
  parts,
  form,
  walking = false,
  heading = 0,
  walkingRef,
  headingRef,
  scale = 5.4,
}: {
  parts: PartRef[]
  form?: BeastForm
  walking?: boolean
  heading?: number
  walkingRef?: { current: boolean }
  headingRef?: { current: number }
  scale?: number
}) {
  const root = useRef<THREE.Group>(null)
  const legs = useRef<THREE.Group>(null)
  const clay = { ...defaultBeastForm(), ...form }
  const bodySrc = sourceOf(parts, 'body')
  const headSrc = sourceOf(parts, 'head')
  const tailSrc = sourceOf(parts, 'tail')
  const legMat = useMemo(() => mat(EMBROIDERY.ink), [])

  useFrame(({ clock }) => {
    const g = root.current
    if (!g) return
    const walk = walkingRef?.current ?? walking
    g.rotation.y = headingRef?.current ?? heading
    const t = clock.elapsedTime
    const bob = walk ? Math.abs(Math.sin(t * 8)) * 0.06 : Math.sin(t * 2) * 0.015
    g.position.y = bob
    if (legs.current) {
      const swing = walk ? Math.sin(t * 8) * 0.45 : 0
      legs.current.children.forEach((leg, i) => {
        leg.rotation.z = swing * (i % 2 === 0 ? 1 : -1)
      })
    }
  })

  return (
    <group ref={root} scale={scale}>
      <group scale={[clay.bodyLength, clay.bodyScale, clay.bodyScale]}>
        <Body source={bodySrc} tint={clay.tint} />
      </group>
      <group scale={[clay.headScale * clay.headStretch, clay.headScale, clay.headScale]}>
        <Head source={headSrc} clay={clay} />
      </group>
      <group scale={clay.tailScale} rotation={[0, 0, clay.tailCurl]}>
        <Tail source={tailSrc} tint={clay.tint} />
      </group>
      <group ref={legs} scale={[1, clay.bodyScale, 1]}>
        {([[0.24, 0.16], [0.24, -0.16], [-0.24, 0.16], [-0.24, -0.16]] as const).map(([x, z], i) => (
          <Stroke key={i} material={legMat} position={[x, 0.02, z]}>
            <capsuleGeometry args={[0.048, 0.26, 4, 6]} />
          </Stroke>
        ))}
      </group>
    </group>
  )
}
